// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Output delivery helpers.
 *
 * Prefers the File System Access API where the browser supports it (extract a
 * whole archive into a chosen folder; save a built zip with a real dialog), and
 * falls back to ordinary blob downloads everywhere else (Firefox, Safari).
 *
 * Nothing here touches the network — every byte is written to the user's disk.
 */

import type { ExtractedFile } from './types';

/** Feature detection — true if the directory picker is usable. */
export function supportsDirectoryPicker(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker ===
    'function';
}

/** Feature detection — true if the save-file picker is usable. */
export function supportsSavePicker(): boolean {
  return typeof (window as unknown as { showSaveFilePicker?: unknown }).showSaveFilePicker ===
    'function';
}

interface DirHandle {
  getDirectoryHandle(name: string, opts: { create: boolean }): Promise<DirHandle>;
  getFileHandle(name: string, opts: { create: boolean }): Promise<FileHandle>;
}
interface FileHandle {
  createWritable(): Promise<{ write(data: BufferSource): Promise<void>; close(): Promise<void> }>;
}

/**
 * Write extracted files into a directory the user picks. Creates nested
 * subdirectories as needed. Returns the number of files written.
 * Throws `AbortError` if the user cancels the picker.
 */
export async function saveToDirectory(files: ExtractedFile[]): Promise<number> {
  const picker = (window as unknown as {
    showDirectoryPicker(opts?: { mode?: string }): Promise<DirHandle>;
  }).showDirectoryPicker;
  const root = await picker({ mode: 'readwrite' });

  let written = 0;
  for (const file of files) {
    const segments = file.path.split('/').filter(Boolean);
    const fileName = segments.pop();
    if (!fileName) continue;
    let dir = root;
    for (const seg of segments) {
      dir = await dir.getDirectoryHandle(seg, { create: true });
    }
    const handle = await dir.getFileHandle(fileName, { create: true });
    const writable = await handle.createWritable();
    await writable.write(file.data as unknown as BufferSource);
    await writable.close();
    written++;
  }
  return written;
}

/** Trigger a single-file blob download. */
export function downloadBlob(data: Uint8Array | Blob, filename: string, mime = 'application/octet-stream'): void {
  const blob = data instanceof Blob ? data : new Blob([data as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/**
 * Save a built archive. Uses the save dialog when available so the user picks
 * the location; otherwise falls back to a blob download. Returns true if saved,
 * false if the user cancelled.
 */
export async function saveArchive(data: Uint8Array, filename: string): Promise<boolean> {
  if (supportsSavePicker()) {
    try {
      const picker = (window as unknown as {
        showSaveFilePicker(opts: unknown): Promise<FileHandle>;
      }).showSaveFilePicker;
      const handle = await picker({
        suggestedName: filename,
        types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
      });
      const writable = await handle.createWritable();
      await writable.write(data as unknown as BufferSource);
      await writable.close();
      return true;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return false;
      // Fall through to download on any other failure.
    }
  }
  downloadBlob(data, filename, 'application/zip');
  return true;
}

/** Share a built archive via the Web Share API (mobile). Returns true if shared. */
export async function shareArchive(data: Uint8Array, filename: string): Promise<boolean> {
  const nav = navigator as Navigator & {
    canShare?: (data: { files: File[] }) => boolean;
    share?: (data: { files: File[]; title?: string }) => Promise<void>;
  };
  if (!nav.share || !nav.canShare) return false;
  const file = new File([data as unknown as BlobPart], filename, { type: 'application/zip' });
  if (!nav.canShare({ files: [file] })) return false;
  await nav.share({ files: [file], title: filename });
  return true;
}
